import { createFileRoute, Link, useNavigate, useRouter, notFound } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { openProDrawer } from "@/components/ProDrawer";
import { isGroupLimitError } from "@/lib/group-limits";
import {
  Globe, Lock, Mail, Pin, MessageSquare, Users, Settings as SettingsIcon,
  Flag, Plus, MessagesSquare, ArrowLeft, Crown, Upload, Loader2, MoreHorizontal, Info, ChevronRight,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/AppShell";
import { FloatingPortal } from "@/components/ui/floating-portal";
import { Linkify } from "@/components/Linkify";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { GroupAdminDialog } from "@/components/groups/GroupAdminDialog";
import { ReportDialog } from "@/components/groups/ReportDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  listGroupMembers, listGroupPosts, listGroupRules,
  joinGroup, leaveGroup, requestJoinGroup, getMyGroupMembership,
  createGroupPost, updateGroup,
  type GroupRow,
} from "@/lib/groups.functions";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";

export const Route = createFileRoute("/comunidades/$slug/")({
  head: ({ params, loaderData }) => {
    const g = loaderData as any;
    const title = g?.name ? `${g.name} — Comunidades Viny Club` : `Comunidade ${params.slug} — Viny Club`;
    const desc = (g?.description || "Comunidade de colecionadores de vinil na Viny Club.").slice(0, 155);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        ...(g?.banner_url ? [{ property: "og:image", content: g.banner_url }] : []),
      ],
    };
  },
  loader: async ({ params }) => {
    const { getGroupBySlug } = await import("@/lib/groups.functions");
    const g = await getGroupBySlug({ data: { slug: params.slug } });
    if (!g) throw notFound();
    return g;
  },
  component: ComunidadePage,
  notFoundComponent: () => (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <h2 className="mb-2 text-xl font-semibold">Comunidade não encontrada</h2>
        <Link to="/comunidades" className="text-sm text-primary hover:underline">Voltar às comunidades</Link>
      </div>
    </AppShell>
  ),
  errorComponent: ({ error }) => (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-20 text-center text-sm text-muted-foreground">
        {(error as Error).message}
      </div>
    </AppShell>
  ),
});

const PRIVACY = {
  public: { Icon: Globe, label: "público" },
  private: { Icon: Lock, label: "privado" },
  invite_only: { Icon: Mail, label: "por convite" },
} as const;

function Panel({ className, children, id }: { className?: string; children: React.ReactNode; id?: string }) {
  return (
    <div id={id} className={cn("rounded-xl border border-[color:var(--hairline)] bg-surface/40", className)}>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-[13px]">
      <span className="shrink-0 font-semibold text-muted-foreground">{label}:</span>
      <span className="min-w-0 truncate">{value}</span>
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" });
}

function ComunidadePage() {
  const group = Route.useLoaderData() as unknown as GroupRow;
  const { profile, isAuthenticated } = useCurrentUser();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const fetchMembership = useServerFn(getMyGroupMembership);
  const fetchPosts = useServerFn(listGroupPosts);
  const fetchMembers = useServerFn(listGroupMembers);
  const fetchRules = useServerFn(listGroupRules);
  const joinFn = useServerFn(joinGroup);
  const leaveFn = useServerFn(leaveGroup);
  const reqJoinFn = useServerFn(requestJoinGroup);

  const memQ = useQuery({
    queryKey: ["group-membership", group.id],
    queryFn: () => fetchMembership({ data: { groupId: group.id } }),
    enabled: isAuthenticated,
  });
  const isMember = !!memQ.data;
  const isAdmin = memQ.data?.role === "admin";

  const topicsQ = useQuery({
    queryKey: ["group-posts", group.id, profile?.id],
    queryFn: () => fetchPosts({ data: { groupId: group.id, userId: profile?.id } }),
  });
  const membersQ = useQuery({
    queryKey: ["group-members", group.id],
    queryFn: () => fetchMembers({ data: { groupId: group.id } }),
  });
  const rulesQ = useQuery({
    queryKey: ["group-rules", group.id],
    queryFn: () => fetchRules({ data: { groupId: group.id } }),
  });

  const [adminOpen, setAdminOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<"forum" | "about" | "members">("forum");

  const joinMut = useMutation({
    mutationFn: () => {
      if (group.privacy === "public") return joinFn({ data: { groupId: group.id } });
      if (group.privacy === "private") return reqJoinFn({ data: { groupId: group.id } });
      throw new Error("Esta comunidade é apenas por convite");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group-membership", group.id] });
      qc.invalidateQueries({ queryKey: ["group-members", group.id] });
      qc.invalidateQueries({ queryKey: ["my-groups"] });
      toast.success(group.privacy === "public" ? "Você entrou na comunidade!" : "Solicitação enviada");
    },
    onError: (e: Error) => {
      if (isGroupLimitError(e)) {
        toast.error(e.message);
        openProDrawer();
        return;
      }
      toast.error(e.message);
    },
  });

  const leaveMut = useMutation({
    mutationFn: () => leaveFn({ data: { groupId: group.id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group-membership", group.id] });
      qc.invalidateQueries({ queryKey: ["group-members", group.id] });
      qc.invalidateQueries({ queryKey: ["my-groups"] });
      toast.success("Você saiu da comunidade");
    },
  });

  const Priv = PRIVACY[group.privacy];
  const topics = topicsQ.data ?? [];
  const members = membersQ.data ?? [];
  const rank = { admin: 0, moderator: 1, member: 2 } as const;
  const sortedMembers = [...members].sort(
    (a, b) => (rank[a.role as keyof typeof rank] ?? 3) - (rank[b.role as keyof typeof rank] ?? 3),
  );
  const creator = members.find((m) => m.user_id === group.created_by);

  const membershipLabel = group.privacy === "invite_only"
    ? "Apenas por convite"
    : group.privacy === "private"
      ? "Solicitar entrada"
      : "Participar";

  return (
    <AppShell>
      <div className="min-h-[calc(100dvh-64px)] bg-background pb-32 md:hidden">
        <section className="relative isolate h-[44dvh] min-h-[310px] max-h-[420px] overflow-hidden bg-surface-elevated">
          {group.banner_url ? (
            <img src={group.banner_url} alt={`Capa de ${group.name}`} className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 aurora-gradient" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-background/65 via-transparent to-background" />
          <div className="absolute inset-x-0 top-0 z-10 grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-2 px-3 pt-3">
            <Button asChild variant="secondary" size="icon" className="h-10 w-10 rounded-full bg-background/65 backdrop-blur-xl">
              <Link to="/comunidades" aria-label="Voltar às comunidades"><ArrowLeft className="h-5 w-5" /></Link>
            </Button>
            <span className="truncate text-center text-xs font-semibold text-foreground/80">Comunidade</span>
            {isMember && (
              <Button asChild size="sm" className="h-10 gap-1.5 rounded-full px-3.5 text-xs font-semibold">
                <Link to="/comunidades/$slug/chat" params={{ slug: group.slug }} aria-label="Chat da comunidade"><MessageSquare className="h-4 w-4" /> Chat</Link>
              </Button>
            )}
            <Button variant="secondary" size="icon" className="h-10 w-10 rounded-full bg-background/65 backdrop-blur-xl" onClick={() => setMoreOpen(true)} aria-label="Mais ações">
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </div>


          {isAdmin && (
            <div className="absolute right-3 top-16 z-10">
              <CoverChanger groupId={group.id} slug={group.slug} kind="banner" label="Capa" className="inline-flex h-9 items-center gap-1.5 rounded-full border border-foreground/10 bg-background/70 px-3 text-xs font-semibold backdrop-blur-xl" />
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 z-10 px-4 pb-5">
            <div className="flex items-end gap-3">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border-2 border-background bg-surface">
                {group.avatar_url ? <img src={group.avatar_url} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-xl font-semibold">{group.name[0]?.toUpperCase()}</div>}
              </div>
              <div className="min-w-0 flex-1 pb-0.5">
                <h1 className="text-balance font-display text-2xl font-semibold leading-tight">{group.name}</h1>
                <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Priv.Icon className="h-3 w-3" />
                  <span>{group.member_count} {group.member_count === 1 ? "membro" : "membros"}</span>
                  {group.category && <><span>·</span><span className="truncate">{group.category}</span></>}
                </div>
              </div>
            </div>
          </div>
        </section>

        {!isMember && (
          <div className="px-4 pt-4">
            <Button className="h-12 w-full rounded-xl" disabled={joinMut.isPending || group.privacy === "invite_only"} onClick={() => isAuthenticated ? joinMut.mutate() : navigate({ to: "/auth" })}>
              {membershipLabel}
            </Button>
          </div>
        )}

        <nav className="sticky top-16 z-20 mt-3 grid grid-cols-3 border-b border-foreground/10 bg-background/95 px-4 backdrop-blur-xl" aria-label="Conteúdo da comunidade">
          {([
            ["forum", "Tópicos"],
            ["about", "Sobre"],
            ["members", `Membros ${members.length}`],
          ] as const).map(([value, label]) => (
            <Button key={value} variant="ghost" className={cn("relative h-12 rounded-none text-xs text-muted-foreground", mobileTab === value && "text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-foreground")} onClick={() => setMobileTab(value)}>
              {label}
            </Button>
          ))}
        </nav>

        <div className="px-4 pt-5">
          {mobileTab === "forum" && (
            <section>
              <div className="mb-2 flex items-center justify-between"><h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Conversas recentes</h2><span className="text-xs text-muted-foreground">{topics.length}</span></div>
              {topicsQ.isLoading ? Array.from({ length: 4 }).map((_, index) => <div key={index} className="h-20 animate-pulse border-b border-foreground/10 bg-surface/20" />) : topics.length === 0 ? (
                <div className="py-16 text-center"><MessagesSquare className="mx-auto h-8 w-8 text-muted-foreground" /><p className="mt-3 text-sm font-medium">Nenhum tópico ainda</p><p className="mt-1 text-xs text-muted-foreground">{isMember ? "Comece a primeira conversa." : "Participe para abrir um assunto."}</p></div>
              ) : topics.map((topic) => (
                <Link key={topic.id} to="/comunidades/$slug/topico/$id" params={{ slug: group.slug, id: topic.id }} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-foreground/10 py-4 active:bg-foreground/[0.03]">
                  <div className="min-w-0"><div className="flex items-center gap-2">{topic.pinned && <Pin className="h-3.5 w-3.5 shrink-0" />}<h3 className="truncate text-[14px] font-semibold">{topic.title || topic.body?.slice(0, 90) || "Sem título"}</h3></div><p className="mt-1 truncate text-xs text-muted-foreground">@{topic.author?.username ?? "?"} · {topic.comments_count} {topic.comments_count === 1 ? "resposta" : "respostas"}</p></div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
                </Link>
              ))}
            </section>
          )}

          {mobileTab === "about" && (
            <section className="space-y-6">
              {group.description && <div><h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Sobre</h2><p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80"><Linkify text={group.description} /></p></div>}
              <div className="grid grid-cols-2 gap-x-5 gap-y-4 border-y border-foreground/10 py-5 text-sm">
                <div><span className="block text-[11px] text-muted-foreground">Criada em</span>{fmtDate(group.created_at)}</div>
                <div><span className="block text-[11px] text-muted-foreground">Local</span>{group.location || "Brasil"}</div>
                <div><span className="block text-[11px] text-muted-foreground">Privacidade</span><span className="inline-flex items-center gap-1"><Priv.Icon className="h-3 w-3" /> {Priv.label}</span></div>
                <div><span className="block text-[11px] text-muted-foreground">Criada por</span>{creator?.profile?.display_name || creator?.profile?.username || "—"}</div>
              </div>
              {(rulesQ.data?.length ?? 0) > 0 && <div><h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Regras</h2><ol className="space-y-3">{rulesQ.data?.map((rule, index) => <li key={rule.id} className="flex gap-3 text-sm"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-surface text-xs text-muted-foreground">{index + 1}</span><span className="pt-0.5">{rule.text}</span></li>)}</ol></div>}
            </section>
          )}

          {mobileTab === "members" && (
            <section>
              <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Pessoas da comunidade</h2>
              {sortedMembers.map((member) => (
                <Link key={member.user_id} to="/profile/$username" params={{ username: member.profile?.username ?? "" }} className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3 border-b border-foreground/10 py-3">
                  <img src={member.profile?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${member.profile?.username ?? "u"}`} alt="" className="h-11 w-11 rounded-xl object-cover" loading="lazy" decoding="async" />
                  <div className="min-w-0"><div className="truncate text-sm font-medium">{member.profile?.display_name || member.profile?.username || "?"}</div><div className="truncate text-xs text-muted-foreground">@{member.profile?.username || "?"}</div></div>
                  {member.role === "admin" ? <span className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-1 text-[9px] font-bold uppercase text-foreground"><Crown className="h-3 w-3" /> Admin</span> : member.role === "moderator" ? <span className="rounded-full bg-surface px-2 py-1 text-[9px] font-bold uppercase text-muted-foreground">Mod</span> : null}
                </Link>
              ))}
            </section>
          )}
        </div>

        {isMember && (
          <FloatingPortal>
            <Button className="fixed bottom-[calc(env(safe-area-inset-bottom)+96px)] right-4 z-[60] h-14 w-14 rounded-full p-0 shadow-[0_16px_40px_-12px_color-mix(in_oklab,var(--action)_55%,transparent)] md:hidden" onClick={() => setTopicOpen(true)} aria-label="Criar tópico"><Plus className="h-5 w-5" /></Button>
          </FloatingPortal>
        )}

        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetContent side="bottom" className="rounded-t-3xl border-foreground/10 px-4 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-3">
            <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-foreground/20" />
            <SheetHeader className="text-left"><SheetTitle>Ações da comunidade</SheetTitle></SheetHeader>
            <div className="mt-5 grid gap-2">

              {isAdmin && <Button asChild variant="secondary" className="h-12 justify-start rounded-xl"><Link to="/comunidades/$slug/admin" params={{ slug: group.slug }}><SettingsIcon className="h-4 w-4" /> Administrar</Link></Button>}
              <Button variant="secondary" className="h-12 justify-start rounded-xl" onClick={() => { setMoreOpen(false); setMobileTab("about"); }}><Info className="h-4 w-4" /> Sobre e regras</Button>
              <Button variant="secondary" className="h-12 justify-start rounded-xl" onClick={() => { setMoreOpen(false); setReportOpen(true); }}><Flag className="h-4 w-4" /> Denunciar</Button>
              {isMember && <Button variant="ghost" className="h-12 justify-start rounded-xl text-destructive" onClick={() => { setMoreOpen(false); leaveMut.mutate(); }}>Sair da comunidade</Button>}
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="mx-auto hidden w-full max-w-5xl px-4 pb-16 md:block">
        {/* Voltar */}
        <Link
          to="/comunidades"
          className="mb-2 mt-1 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar às comunidades
        </Link>

        {/* Capa da comunidade */}
        {(group.banner_url || isAdmin) && (
          <Panel className="relative mb-3 overflow-hidden p-0">
            <div className="aspect-[4/1] w-full bg-surface">
              {group.banner_url ? (
                <img
                  src={group.banner_url}
                  alt={`Capa de ${group.name}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-r from-primary/25 via-surface to-surface" />
              )}
            </div>
            {isAdmin && (
              <div className="absolute bottom-2 right-2">
                <CoverChanger
                  groupId={group.id}
                  slug={group.slug}
                  kind="banner"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-background/85 px-3 py-1.5 text-[12px] font-semibold backdrop-blur transition-colors hover:bg-background disabled:opacity-60"
                />
              </div>
            )}
          </Panel>
        )}

        {/* Faixa com o nome da comunidade */}
        <Panel className="mb-3 flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <h1 className="font-display text-lg font-semibold tracking-[-0.01em] sm:text-xl">{group.name}</h1>

          <div className="flex flex-wrap items-center gap-2">
            {isMember && (
              <Link
                to="/comunidades/$slug/chat"
                params={{ slug: group.slug }}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-[12.5px] font-semibold hover:bg-surface"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Chat
              </Link>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setAdminOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-[12.5px] font-semibold hover:bg-surface"
              >
                <SettingsIcon className="h-3.5 w-3.5" /> Admin
              </button>
            )}
            {isMember ? (
              <button
                onClick={() => leaveMut.mutate()}
                disabled={leaveMut.isPending}
                className="rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-destructive"
              >
                Sair
              </button>
            ) : (
              <button
                onClick={() => (isAuthenticated ? joinMut.mutate() : navigate({ to: "/auth" }))}
                disabled={joinMut.isPending || group.privacy === "invite_only"}
                className="rounded-full bg-primary px-4 py-1.5 text-[12.5px] font-semibold text-primary-foreground disabled:opacity-50"
              >
                {group.privacy === "invite_only"
                  ? "Apenas por convite"
                  : group.privacy === "private"
                  ? "Solicitar entrada"
                  : "Participar"}
              </button>
            )}
          </div>
        </Panel>

        <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
          {/* Coluna da esquerda */}
          <div className="space-y-3">
            <Panel className="overflow-hidden p-2">
              <div className="aspect-square w-full overflow-hidden rounded-lg bg-surface">
                {group.avatar_url ? (
                  <img src={group.avatar_url} alt={group.name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full w-full place-items-center bg-gradient-to-br from-primary/20 via-surface to-surface">
                    <Users className="h-10 w-10 text-muted-foreground/60" strokeWidth={1.4} />
                  </div>
                )}
              </div>
              {isAdmin && <CoverChanger groupId={group.id} slug={group.slug} />}
            </Panel>

            <Panel className="p-4">
              <h2 className="mb-3 text-[15px] font-semibold leading-snug">{group.name}</h2>
              <nav className="space-y-1.5 text-[13px]">
                <a href="#comunidade" className="block text-primary hover:underline">comunidade</a>
                <a href="#forum" className="block text-muted-foreground hover:text-foreground">fórum</a>
                <button
                  type="button"
                  onClick={() => setReportOpen(true)}
                  className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  <Flag className="h-3.5 w-3.5" /> denunciar abuso
                </button>
              </nav>
            </Panel>

            <Panel className="p-4">
              <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Membros ({membersQ.data?.length ?? group.member_count})
              </h3>
              <div className="space-y-2">
                {sortedMembers.slice(0, 8).map((m) => (
                  <Link
                    key={m.user_id}
                    to="/profile/$username"
                    params={{ username: m.profile?.username ?? "" }}
                    className="flex items-center gap-2 rounded-lg p-1 hover:bg-surface"
                  >
                    <img
                      loading="lazy"
                      decoding="async"
                      src={m.profile?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${m.profile?.username ?? "u"}`}
                      alt=""
                      className="h-7 w-7 rounded-md object-cover"
                    />
                    <span className="min-w-0 flex-1 truncate text-[12.5px]">
                      {m.profile?.display_name || m.profile?.username || "?"}
                    </span>
                    {m.role === "admin" ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400">
                        <Crown className="h-2.5 w-2.5" /> admin
                      </span>
                    ) : m.role === "moderator" ? (
                      <span className="shrink-0 rounded-full border border-[color:var(--hairline)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        mod
                      </span>
                    ) : null}
                  </Link>
                ))}
                {(membersQ.data?.length ?? 0) === 0 && (
                  <p className="text-[12.5px] text-muted-foreground">Ainda sem membros.</p>
                )}
              </div>
            </Panel>
          </div>

          {/* Coluna da direita */}
          <div className="min-w-0 space-y-3">
            <Panel id="comunidade" className="p-4">
              <h2 className="mb-3 text-[15px] font-semibold">{group.name}</h2>
              <div className="grid gap-1.5 sm:grid-cols-2">
                <Field label="idioma" value="Português (Brasil)" />
                <Field label="criada em" value={fmtDate(group.created_at)} />
                <Field label="categoria" value={group.category || "Vinil"} />
                <Field label="local" value={group.location || "Brasil"} />
                <Field
                  label="tipo"
                  value={
                    <span className="inline-flex items-center gap-1">
                      <Priv.Icon className="h-3 w-3" /> {Priv.label}
                    </span>
                  }
                />
                <Field label="membros" value={group.member_count} />
                <Field
                  label="criada por"
                  value={
                    creator?.profile?.username ? (
                      <Link
                        to="/profile/$username"
                        params={{ username: creator.profile.username }}
                        className="text-primary hover:underline"
                      >
                        {creator.profile.display_name || `@${creator.profile.username}`}
                      </Link>
                    ) : (
                      "—"
                    )
                  }
                />
              </div>
            </Panel>

            {group.description && (
              <Panel className="p-4">
                <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-muted-foreground">
                  <Linkify text={group.description} />
                </p>
              </Panel>
            )}

            {(rulesQ.data?.length ?? 0) > 0 && (
              <Panel className="p-4">
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Regras</h3>
                <ol className="space-y-1 text-[13px]">
                  {rulesQ.data!.map((r, i) => (
                    <li key={r.id} className="flex gap-2">
                      <span className="text-muted-foreground">{i + 1}.</span>
                      <span>{r.text}</span>
                    </li>
                  ))}
                </ol>
              </Panel>
            )}

            {/* Fórum */}
            <Panel id="forum" className="overflow-hidden">
              <div className="flex items-center justify-between gap-3 border-b border-[color:var(--hairline)] px-4 py-3">
                <h2 className="flex items-center gap-2 text-[15px] font-semibold text-primary">
                  <MessagesSquare className="h-4 w-4" /> Fórum
                </h2>
                {isMember && (
                  <button
                    type="button"
                    onClick={() => setTopicOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-[12.5px] font-semibold hover:bg-surface"
                  >
                    <Plus className="h-3.5 w-3.5" /> Novo tópico
                  </button>
                )}
              </div>

              {topicsQ.isLoading ? (
                <div className="divide-y divide-[color:var(--hairline)]">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-14 animate-pulse bg-white/[0.02]" />
                  ))}
                </div>
              ) : topics.length === 0 ? (
                <div className="px-4 py-10 text-center text-[13px] text-muted-foreground">
                  Nenhum tópico ainda. {isMember ? "Abra o primeiro assunto." : "Participe para começar um assunto."}
                </div>
              ) : (
                <ul className="divide-y divide-[color:var(--hairline)]">
                  {topics.map((t) => (
                    <li key={t.id}>
                      <Link
                        to="/comunidades/$slug/topico/$id"
                        params={{ slug: group.slug, id: t.id }}
                        className="block px-4 py-3 transition-colors hover:bg-white/[0.03]"
                      >
                        <div className="flex items-center gap-2">
                          {t.pinned && <Pin className="h-3.5 w-3.5 shrink-0 text-primary" />}
                          <span className="min-w-0 flex-1 truncate text-[13.5px] text-primary">
                            {t.title || t.body?.slice(0, 90) || "Sem título"}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-muted-foreground">
                          <span>
                            {t.comments_count} {t.comments_count === 1 ? "resposta" : "respostas"}
                          </span>
                          <span className="truncate">@{t.author?.username ?? "?"}</span>
                          <span>{fmtDate(t.created_at)}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </div>
      </div>

      {isAdmin && <GroupAdminDialog open={adminOpen} onOpenChange={setAdminOpen} group={group} />}
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} groupId={group.id} />
      <NewTopicDialog
        open={topicOpen}
        onOpenChange={setTopicOpen}
        groupId={group.id}
        onCreated={() => qc.invalidateQueries({ queryKey: ["group-posts", group.id] })}
      />
    </AppShell>
  );
}

function NewTopicDialog({
  open, onOpenChange, groupId, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  groupId: string;
  onCreated: () => void;
}) {
  const isMobile = useIsMobile();
  const createFn = useServerFn(createGroupPost);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const mut = useMutation({
    mutationFn: () => createFn({ data: { groupId, title, body, type: "discussion" } }),
    onSuccess: () => {
      setTitle("");
      setBody("");
      onCreated();
      onOpenChange(false);
      toast.success("Tópico criado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const form = (
    <div className="space-y-3">
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={120}
        placeholder="Título do tópico"
        className="h-12 w-full rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 text-base focus:border-primary/60 focus:outline-none"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={5}
        maxLength={2000}
        placeholder="Escreva o assunto..."
        className="w-full resize-none rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 py-3 text-base focus:border-primary/60 focus:outline-none"
      />
      <Button
        type="button"
        onClick={() => {
          if (title.trim().length < 3) return toast.error("Dê um título ao tópico");
          if (body.trim().length < 3) return toast.error("Escreva o assunto");
          mut.mutate();
        }}
        disabled={mut.isPending}
        className="h-12 w-full rounded-xl"
      >
        {mut.isPending ? "Publicando..." : "Publicar tópico"}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="flex h-[92dvh] flex-col overflow-y-auto rounded-t-3xl border-foreground/10 px-4 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-3">
          <div className="mx-auto mb-5 h-1 w-10 shrink-0 rounded-full bg-foreground/20" />
          <SheetHeader className="mb-5 shrink-0 text-left"><SheetTitle>Novo tópico</SheetTitle></SheetHeader>
          {form}
        </SheetContent>

      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Novo tópico</DialogTitle></DialogHeader>
        {form}
      </DialogContent>
    </Dialog>
  );
}

function CoverChanger({
  groupId,
  slug,
  kind = "avatar",
  label,
  className,
}: {
  groupId: string;
  slug: string;
  kind?: "avatar" | "banner";
  label?: string;
  className?: string;
}) {
  const qc = useQueryClient();
  const router = useRouter();
  const updateFn = useServerFn(updateGroup);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(file: File) {
    if (!file.type.startsWith("image/")) return toast.error("Selecione uma imagem");
    if (file.size > 6 * 1024 * 1024) return toast.error("Máximo 6MB");
    setUploading(true);
    try {
      const { data: sess } = await supabase.auth.getUser();
      const uid = sess.user?.id;
      if (!uid) throw new Error("Entre na sua conta para enviar imagens");
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${uid}/groups/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("community-media")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw upErr;
      const { data: signed, error: sErr } = await supabase.storage
        .from("community-media")
        .createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      if (sErr || !signed) throw sErr ?? new Error("Falha ao gerar URL");
      await updateFn({
        data:
          kind === "banner"
            ? { groupId, banner_url: signed.signedUrl }
            : { groupId, avatar_url: signed.signedUrl },
      });
      await qc.invalidateQueries({ queryKey: ["group", slug] });
      await qc.invalidateQueries({ queryKey: ["groups-public"] });
      await qc.invalidateQueries({ queryKey: ["my-groups"] });
      await router.invalidate();
      toast.success(kind === "banner" ? "Capa atualizada" : "Ícone atualizado");
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao enviar");
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={
          className ??
          "mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[color:var(--hairline)] px-2 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-foreground disabled:opacity-60"
        }
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {uploading ? "Enviando…" : label ?? (kind === "banner" ? "Trocar capa" : "Trocar ícone")}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = "";
        }}
      />
    </>
  );
}

